import { MARKETPLACE_DASHBOARDS_LIST_INITIAL_SIZE, useMarketplaceDashboards } from "@/api/hooks/marketplace";
import { copyMarketplaceDashboard } from "@/api/hooks/profile";
import { CardGroup } from "@/components/molecules/CardGroup/CardGroup";
import "@/marketplace/marketplace.scss";
import { buildAddDashboardErrorToast, buildAddDashboardSuccessToast, useMarketplaceToast } from "@/marketplace/toast";
import { useMarketplaceImage } from "@/marketplace/useMarketplaceImage";
import { useAppDispatch } from "@/reducer";
import { pageMarketplaceDashboard, startsWithExtraPath } from "@/route";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { Button, Card } from "@mm/metx-design-system";
import type { MarketplaceDashboardCard, SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import { type MouseEvent, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

const ADDED_FEEDBACK_MS = 10_000;

const MARKETPLACE_LOADING_PLACEHOLDERS = Array.from(
  { length: MARKETPLACE_DASHBOARDS_LIST_INITIAL_SIZE },
  (_, index) =>
    ({
      id: -(index + 1),
    }) as MarketplaceDashboardCard,
);

function MarketplaceDashboardCardItem({
  dashboard,
  detailHref,
  loading = false,
  onAddSuccess,
  onAddError,
}: {
  dashboard: MarketplaceDashboardCard;
  detailHref?: string;
  loading?: boolean;
  onAddSuccess: (dashboardTitle: string, copiedProfile: SlimProfile) => void;
  onAddError: (description?: string) => void;
}) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isCopying, setIsCopying] = useState(false);
  const [showAdded, setShowAdded] = useState(false);
  const imageSrc = useMarketplaceImage(dashboard.image_url);

  useEffect(() => {
    if (!showAdded) {
      return;
    }

    const timeoutId = window.setTimeout(() => setShowAdded(false), ADDED_FEEDBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [showAdded]);

  const handleAddDashboard = async (event: MouseEvent) => {
    event.stopPropagation();
    if (loading || showAdded || isCopying) {
      return;
    }

    setIsCopying(true);
    try {
      const copiedProfile = await dispatch(copyMarketplaceDashboard(dashboard.id)).unwrap();
      setShowAdded(true);
      onAddSuccess(dashboard.title, copiedProfile);
    } catch (error) {
      console.error("Failed to copy marketplace dashboard:", error);
      onAddError(error instanceof Error ? error.message : undefined);
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <>
      <Card
        className="marketplace-card"
        loading={loading}
        imageSrc={imageSrc}
        imageAlt={dashboard.title}
        href={detailHref}
        onClick={
          detailHref
            ? (event) => {
                event.preventDefault();
                history.push(detailHref);
              }
            : undefined
        }
        title={
          <span className="marketplace-card__title">
            {dashboard.title}
            <span className="material-symbols-outlined marketplace-card__title-icon" aria-hidden>
              arrow_outward
            </span>
          </span>
        }
        footerMeta={dashboard.tier ?? undefined}
        body={dashboard.description ?? ""}
        primaryAction={{
          label: showAdded
            ? t({ id: "marketplace.added", message: "Added" })
            : t({ id: "marketplace.add-dashboard", message: "Add dashboard" }),
          icon: showAdded ? (
            <span className="material-symbols-outlined">check</span>
          ) : isCopying ? (
            <span className="material-symbols-outlined marketplace-card__action-icon--spin">progress_activity</span>
          ) : (
            <span className="material-symbols-outlined">add</span>
          ),
          disabled: showAdded || isCopying,
          onClick: handleAddDashboard,
        }}
      />
    </>
  );
}

export function MarketplaceDashboardList() {
  const { i18n } = useLingui();
  const location = useLocation();
  const routePrefix = startsWithExtraPath(location.pathname);
  const { showToast, toast } = useMarketplaceToast();
  const { isLoading, isError, isEmpty, items, total, hasMore, isLoadingMore, loadMore, refetch } =
    useMarketplaceDashboards();
  const dashboards = isLoading ? MARKETPLACE_LOADING_PLACEHOLDERS : items;

  return (
    <section className="marketplace">
      {toast}
      <header className="marketplace__header">
        <h2 className="marketplace__heading">
          <Trans id="marketplace.heading">Marketplace dashboards</Trans>
        </h2>
        {!isLoading && !isEmpty && (
          <p className="marketplace__count">
            {i18n._({
              id: "marketplace.showing-count",
              message: "Showing {0} of {1} templates",
              values: { 0: items.length, 1: total },
            })}
          </p>
        )}
      </header>

      {isError ? (
        <div className="marketplace__error">
          <p className="marketplace__error-message">
            {t({
              id: "marketplace.load-error",
              message: "Could not load marketplace dashboards.",
            })}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            {t({ id: "marketplace.retry", message: "Try again" })}
          </Button>
        </div>
      ) : isEmpty ? (
        <p className="marketplace__empty">
          <Trans id="marketplace.empty">There are no dashboards available right now.</Trans>
        </p>
      ) : (
        <>
          <CardGroup layout="grid">
            {dashboards.map((dashboard) => (
              <MarketplaceDashboardCardItem
                key={dashboard.id}
                dashboard={dashboard}
                loading={isLoading}
                detailHref={isLoading ? undefined : `${routePrefix}${pageMarketplaceDashboard.asString(dashboard.id)}`}
                onAddSuccess={(dashboardTitle, copiedProfile) =>
                  showToast(buildAddDashboardSuccessToast(dashboardTitle, copiedProfile, routePrefix))
                }
                onAddError={(description) => showToast(buildAddDashboardErrorToast(description))}
              />
            ))}
          </CardGroup>

          {!isEmpty && hasMore && (
            <div className="marketplace__footer">
              <Button variant="outline" disabled={isLoadingMore} onClick={loadMore}>
                {isLoadingMore ? <Trans>Loading...</Trans> : <Trans>Show more</Trans>}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
